import { z } from 'zod';
import {
  DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MIN_LENGTH,
  MAX_FLOOR,
  MESSAGE_MAX_LENGTH,
  MIN_FLOOR,
  TICKET_CATEGORY_IDS,
  TICKET_STATUSES,
} from './constants';
import { normalizeUnitNumber, UNIT_NUMBER_PATTERN } from './units';

export const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export const unitNumberSchema = z
  .string({ error: 'Please enter your unit number.' })
  .transform(normalizeUnitNumber)
  .refine((v) => v.length > 0, 'Please enter your unit number.')
  .refine((v) => UNIT_NUMBER_PATTERN.test(v), 'Use a unit number like 101, A-101 or B1203.');

export const nameSchema = z
  .string()
  .trim()
  .min(2, 'Please enter your full name.')
  .max(80, 'Please keep your name under 80 characters.');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Please enter a valid email address.'));

export const passwordSchema = z
  .string()
  .min(8, 'Use at least 8 characters.')
  .max(128, 'Please use 128 characters or fewer.');

export const countryCodeSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{0,3}$/, 'Use a country code like +1.');

export const nationalPhoneSchema = z
  .string()
  .transform((v) => v.replace(/[\s()-]/g, ''))
  .refine((v) => /^\d{6,14}$/.test(v), 'Please enter a valid phone number.');

export function toE164(countryCode: string, national: string): string {
  return `${countryCode.trim()}${national.replace(/[\s()-]/g, '').replace(/^0+/, '')}`;
}

export const signUpSchema = z
  .object({
    name: nameSchema,
    countryCode: countryCodeSchema,
    phone: nationalPhoneSchema,
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
    unitNumber: unitNumberSchema,
  })
  .superRefine((v, ctx) => {
    if (!E164_PATTERN.test(toE164(v.countryCode, v.phone))) {
      ctx.addIssue({
        code: 'custom',
        path: ['phone'],
        message: 'Please enter a valid phone number.',
      });
    }
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    error: "These passwords don't match.",
    // Show the mismatch even when other fields still have errors.
    when: ({ issues }) =>
      !issues.some((i) => i.path?.[0] === 'password' || i.path?.[0] === 'confirmPassword'),
  });
export type SignUpInput = z.input<typeof signUpSchema>;
export type SignUpValues = z.output<typeof signUpSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Please enter your password.'),
});
export type LoginValues = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });

/** New password chosen from the emailed reset link (/auth/action, D34). */
export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ['confirmPassword'],
    error: "These passwords don't match.",
    when: ({ issues }) => !issues.some((i) => i.path?.[0] === 'password'),
  });
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export const ticketSchema = z
  .object({
    category: z.enum(TICKET_CATEGORY_IDS, { error: 'Please choose a category.' }),
    title: z.string().trim().max(80, 'Please keep the title under 80 characters.').optional(),
    description: z
      .string()
      .trim()
      .min(1, 'Please describe the issue.')
      .min(
        DESCRIPTION_MIN_LENGTH,
        `Please add a little more detail (at least ${DESCRIPTION_MIN_LENGTH} characters).`,
      )
      .max(DESCRIPTION_MAX_LENGTH, `Please keep it under ${DESCRIPTION_MAX_LENGTH} characters.`),
  })
  .superRefine((v, ctx) => {
    if (v.category === 'other' && (!v.title || v.title.length < 3)) {
      ctx.addIssue({
        code: 'custom',
        path: ['title'],
        message: 'Please add a short title so we know what this is about.',
      });
    }
  });
export type TicketInput = z.input<typeof ticketSchema>;
export type TicketValues = z.output<typeof ticketSchema>;

export const messageSchema = z.object({
  text: z.string().trim().min(1).max(MESSAGE_MAX_LENGTH),
});

const floorSchema = z.number().int().min(MIN_FLOOR).max(MAX_FLOOR);
const reasonSchema = z.string().trim().max(300).optional();
const uidSchema = z.string().min(1).max(128);

export const approveTenantInput = z.object({
  uid: uidSchema,
  replaceExisting: z.boolean().optional(),
});
export const rejectTenantInput = z.object({ uid: uidSchema, reason: reasonSchema });
export const deactivateTenantInput = z.object({ uid: uidSchema, reason: reasonSchema });
export const createUnitInput = z.object({
  unitNumber: unitNumberSchema,
  floor: floorSchema.optional(),
});
export const updateUnitFloorInput = z.object({
  unitId: z.string().min(1),
  floor: floorSchema.nullable(),
});
export const getEmailVerificationInput = z.object({ uids: z.array(uidSchema).max(100) });
export const updateTicketStatusInput = z.object({
  ticketId: z.string().min(1),
  status: z.enum(TICKET_STATUSES),
});

export type ApproveTenantInput = z.infer<typeof approveTenantInput>;
export type RejectTenantInput = z.infer<typeof rejectTenantInput>;
export type DeactivateTenantInput = z.infer<typeof deactivateTenantInput>;
export type CreateUnitInput = z.input<typeof createUnitInput>;
export type UpdateUnitFloorInput = z.infer<typeof updateUnitFloorInput>;
export type UpdateTicketStatusInput = z.infer<typeof updateTicketStatusInput>;
export type GetEmailVerificationInput = z.infer<typeof getEmailVerificationInput>;
/** uid → whether that account has confirmed its email. Unknown uids are left out. */
export type EmailVerificationResult = Record<string, boolean>;
